import React, { useState, useEffect, useCallback } from 'react';
import { useScore } from '../../context/ScoreProvider';
import '../../styles/QuizScreen.css';

export default function MultipleQuizScreen({
  question,
  image,
  images,
  columnDirection,
  options,
  feedback,
  onCheckStateChange,
  screenId,
  screenScore
}) {
  const { submitAnswer } = useScore();
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [isChecked, setIsChecked] = useState(false);

  const toggleSelect = (index) => {
    if (isChecked) return;
    setSelectedIndexes(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const correctIndexes = options
    .map((o, i) => (o.correct ? i : null))
    .filter(i => i !== null);

  const isCorrect =
    selectedIndexes.length === correctIndexes.length &&
    correctIndexes.every(i => selectedIndexes.includes(i));

  const handleCheck = useCallback(() => {
    if (selectedIndexes.length === 0) return;

    setIsChecked(true);
    if (screenScore != null) {
      submitAnswer(screenId, screenScore, isCorrect);
    }
  }, [isCorrect, screenId, screenScore, selectedIndexes, submitAnswer]);

  useEffect(() => {
    onCheckStateChange?.({
      checked: isChecked,
      canCheck: selectedIndexes.length > 0,
      onCheck: handleCheck,
    });
  }, [selectedIndexes, isChecked, handleCheck, onCheckStateChange]);

  const hasLinkedImages = images && Array.isArray(images) && images.length > 0;

  const getImageState = (imageIndex) => {
    if (!isChecked) return 'default';

    const linkedOptionIndex = images[imageIndex]?.linkedTo;
    if (linkedOptionIndex == null) return 'default';

    const isSelected = selectedIndexes.includes(linkedOptionIndex);
    const isOptionCorrect = options[linkedOptionIndex]?.correct;

    if (!isSelected) return 'default';
    return isOptionCorrect ? 'correct' : 'incorrect';
  };

  return (
    <div className="quiz-screen">
      <div className={`quiz-card ${isChecked ? 'blurred' : ''}`}>

        {question && (
          <div
            className={`
              quiz-question
              font-${question?.fontSize ?? 'md'}
              text-${question?.textAlign ?? 'left'}
            `}
            dangerouslySetInnerHTML={{ __html: question.value }}
          />
        )}

        {image && !hasLinkedImages && (
          <div className="quiz-image">
            <img src={image} alt="" />
          </div>
        )}

        <div
          className={`question-answer-wrapper ${
            columnDirection ? 'column-direction' : ''
          }`}
        >
          {hasLinkedImages && (
            <div className="quiz-images-grid">
              {images.map((img, index) => (
                <div
                  key={index}
                  className={`quiz-image-item ${getImageState(index)}`}
                >
                  <img src={img.src} alt="" />
                </div>
              ))}
            </div>
          )}

          <div className="quiz-options">
            {options.map((option, index) => {
              const isSelected = selectedIndexes.includes(index);

              let stateClass = '';
              if (isChecked && isSelected) {
                stateClass = option.correct ? 'correct' : 'incorrect';
              } else if (isSelected) {
                stateClass = 'selected';
              }

              return (
                <button
                  key={index}
                  onClick={() => toggleSelect(index)}
                  className={`quiz-option ${stateClass}`}
                >
                  {option.text}
                </button>
              );
            })}
          </div>
        </div>

        {isChecked && feedback && (
          <div
            className={`quiz-feedback ${
              isCorrect ? 'quiz-feedback-correct' : 'quiz-feedback-incorrect'
            }`}
            dangerouslySetInnerHTML={{
              __html: isCorrect ? feedback.correct : feedback.incorrect
            }}
          />
        )}
      </div>
    </div>
  );
}
